import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import { logger } from '@/utils/logger';
import sanitize from '@/lib/sanitize';
import {
  LayoutItem,
  DashboardWidget,
  Widget,
  WidgetModulesResponse,
  WidgetConfig,
  DefaultJsonProperties,
  AdminWidget,
  DashboardTab,
  ApiResponse,
  ApiDashboardTabWidget,
} from '@/interfaces/Dashboard';
import useFlashMessageStore from '@/stores/flash-messages';
import { hash } from 'ohash';
import useAxiosInstance from '@/composables/axiosInstance';

export const PREVIEW_WIDGET_KEY = 'preview';
export const PENDING_WIDGET_PREFIX = 'pending';

const mapApiWidgetResponseToWidget = (widgetFromAPIResponse: WidgetConfig): Widget => {
  const defaultJsonProperties = widgetFromAPIResponse.defaultJsonProperties as DefaultJsonProperties;
  const defaultVisualType = widgetFromAPIResponse?.defaultVisualType?.toLowerCase();
  const parameters = { ...defaultJsonProperties.parameters };

  // Normalize the withinXMonths key if the API returns it differently
  if (parameters.withinXMonths && !parameters.withinXMonths) {
    parameters.withinXMonths = parameters.withinXMonths;
    delete parameters.withinXMonths; // optional
  }

  const properties = {
    ...defaultJsonProperties,
    availableDisplayTypes: defaultJsonProperties.availableDisplayTypes?.map((d) => d.toLowerCase()),
    currentDisplayType: defaultVisualType,
    currentDisplayUnit: defaultJsonProperties.currentDisplayUnit ?? 'count',
    parameters: {
      ...defaultJsonProperties.parameters,
      previousMonths: defaultJsonProperties.parameters?.previousMonths,
      previousRange: defaultJsonProperties.parameters?.previousRange,
    },
    title: widgetFromAPIResponse.widgetName,
    subtitle: widgetFromAPIResponse.subtitle,
  };

  return {
    guid: widgetFromAPIResponse.guid,
    helpFieldText: sanitize(widgetFromAPIResponse.helpFieldText),
    description: widgetFromAPIResponse.longDescription!,
    label: widgetFromAPIResponse.widgetName,
    title: widgetFromAPIResponse.widgetName,
    subtitle: widgetFromAPIResponse.subtitle,
    dataSourceGuid: widgetFromAPIResponse.dataSourceGuid as string,
    defaultVisualType: widgetFromAPIResponse.defaultVisualType,
    defaultJsonProperties: properties,
  };
};

interface State {
  dashboardTabs: DashboardTab[];
  widgets: Widget[];
  widgetCategoryFilter: string;
  widgetFilter: string;
  dataSourceCache: Map<string, any>;
  widgetParamHashes: Map<string, string>;
  errorLoading: boolean;
}

const useDashboardStore = defineStore({
  id: 'dashboard',
  state: (): State => ({
    dashboardTabs: [],
    widgets: [],
    widgetCategoryFilter: '',
    widgetFilter: '',
    dataSourceCache: new Map(),
    widgetParamHashes: new Map(),
    errorLoading: false,
  }),
  actions: {
    async init() {
      const allOk = await Promise.all([this.getDashboardTabs(), this.getWidgets()]).then((results) =>
        results.every(Boolean)
      );
      this.errorLoading = !allOk;
    },
    async getWidgets(): Promise<boolean> {
      try {
        const { data } = await useAxiosInstance.get<WidgetModulesResponse>('/api/dashboard/modules/widgets/');
        if (data.success) {
          const widgetsFromAPI = data.data
            .filter((w) => {
              return w.dataSourceGuid || w.defaultJsonProperties.componentType === 'Title';
            })
            .map(mapApiWidgetResponseToWidget)
            .sort((a: Widget, b: Widget) => {
              if (!a.title && !b.title) return 0;
              if (!a.title) return 1;
              if (!b.title) return -1;
              return a.title.localeCompare(b.title);
            });

          this.widgets = widgetsFromAPI;
          return true;
        }
      } catch (error) {
        console.error('[DashboardStore] getWidgets error:', error);
      }
      return false;
    },
    async fetchDataSource(
      dataSource: string | undefined,
      options?: { widgetGuid?: string; params?: Record<string, any>; skipCache?: boolean }
    ) {
      if (!dataSource) {
        console.warn('[DashboardStore] fetchDataSource called with undefined dataSource', options);
        return;
      }

      let params: Record<string, any> = {};
      if (options?.widgetGuid) params = { ...params, ...this.getDashboardWidgetParameters(options.widgetGuid) };
      if (options?.params) params = { ...params, ...options.params };

      // Skip cache if explicitly requested
      if (options?.skipCache) {
        logger.log(`🔄 Skipping cache for dataSource: ${dataSource}`);
        try {
          const { data } = await useAxiosInstance.post(`/api/Dashboard/datasources/${dataSource}/data`, params);
          // Optionally update cache with fresh data
          let cacheKey: string;
          if (options?.widgetGuid) {
            cacheKey = options.widgetGuid;
          } else {
            cacheKey = `${dataSource}:${hash(params)}`;
          }
          this.dataSourceCache.set(cacheKey, data);
          return data;
        } catch (error) {
          console.error('[DashboardStore] fetchDataSource error:', error);
        }
        return;
      }

      let cacheKey: string;
      if (options?.widgetGuid) {
        const paramHash = hash(params);
        const lastHash = this.widgetParamHashes.get(options.widgetGuid);

        if (lastHash && lastHash !== paramHash) {
          this.dataSourceCache.delete(options.widgetGuid);
        }

        this.widgetParamHashes.set(options.widgetGuid, paramHash);
        cacheKey = options.widgetGuid;
      } else {
        cacheKey = `${dataSource}:${hash(params)}`;
      }

      if (this.dataSourceCache.has(cacheKey)) {
        logger.log(`💾 Using cached data for: ${dataSource}`);
        return this.dataSourceCache.get(cacheKey);
      }

      try {
        const { data } = await useAxiosInstance.post(`/api/Dashboard/datasources/${dataSource}/data`, params);
        this.dataSourceCache.set(cacheKey, data);
        return data;
      } catch (error) {
        console.error('[DashboardStore] fetchDataSource error:', error);
      }
    },
    async getAdminWidgets(): Promise<AdminWidget[] | null> {
      try {
        const { data } = await useAxiosInstance.get<ApiResponse>('/api/dashboard/widgets/');
        if (data.success) return data.data as AdminWidget[];
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    async updateAdminWidget(widget: AdminWidget): Promise<boolean> {
      const messageStore = useFlashMessageStore();

      try {
        const { data } = await useAxiosInstance.put<ApiResponse>(`/api/Dashboard/widgets/${widget.guid}`, {
          ...widget,
          defaultJsonProperties: JSON.stringify(widget.defaultJsonProperties),
        });
        if (data.success) {
          messageStore.pushNotification({
            id: `widget-${widget.guid}-updated`,
            type: 'success',
            title: 'Widget Settings Updated',
            message: 'The Widget Settings have been updated',
          });
          return true;
        }
      } catch (error) {
        console.error(error);
      }

      messageStore.pushNotification({
        id: `widget-${widget.guid}-updated`,
        type: 'error',
        message: 'There was a problem updating the widget settings',
      });
      return false;
    },
    async updateDashboardTabWidgetSettings(guid: string) {
      const messageStore = useFlashMessageStore();
      try {
        if (this.activeDashboardTab) {
          // const { data } = await useAxiosInstance.put(`/api/Dashboard/Tabs/${this.activeDashboardTab.guid}/widgets/${widget.guid}`, {
          //   defaultJsonProperties: JSON.stringify(widget.defaultJsonProperties),
          // });
          const success = await this.saveDashboardTab(this.activeDashboardTab);
          if (success) {
            messageStore.pushNotification({
              id: `widget-${guid}-updated`,
              type: 'success',
              title: 'Widget Filters Updated',
              message: 'The Widget Filters have been updated',
            });
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error(`[DashboardStore] Error updating widget: ${guid}`, error);
        messageStore.pushNotification({
          id: `widget-${guid}-error`,
          type: 'error',
          title: 'Widget Update Failed',
          message: 'Could not update widget settings',
        });
        return false;
      }
    },
    updateDashboardWidget(widget: DashboardWidget) {
      const activeTab = this.activeDashboardTab;
      if (!activeTab) return;
      activeTab.dashboardTabWidgets =
        activeTab.dashboardTabWidgets?.map((w) =>
          w.dashboardTabWidgetGuid === widget.dashboardTabWidgetGuid ? widget : w
        ) || [];
    },
    async setActiveDashboardTab(dashboardID: string) {
      this.updateDashboardTabOrder(
        this.dashboardTabs.map((tab) => ({
          ...tab,
          isCurrent: tab.guid === dashboardID,
        }))
      );
      if (!this.activeDashboardTab?.synced) {
        await this.syncActiveDashboardTabWidgets();
      }
    },
    async removeActiveDashboard() {
      const activeTab = this.activeDashboardTab;
      if (!activeTab) return;

      const currentIndex = this.dashboardTabs.findIndex((tab) => tab.guid === activeTab.guid);

      const deleted = await this.deleteDashboardTab(activeTab.guid!);
      if (!deleted) return;

      // Remove deleted tab from the list
      this.dashboardTabs = this.dashboardTabs.filter((tab) => tab.guid !== activeTab.guid);

      if (this.dashboardTabs.length > 0) {
        // Prefer the next tab, otherwise fallback to previous
        const newIndex = currentIndex < this.dashboardTabs.length ? currentIndex : this.dashboardTabs.length - 1;

        this.updateDashboardTabOrder(
          this.dashboardTabs.map((tab, index) => ({
            ...tab,
            isCurrent: index === newIndex,
          }))
        );
      } else {
        await this.getDashboardTabs();
      }
    },
    addDashboardTabWidget(widget: Widget, id: string) {
      if (!this.activeDashboardTab) return;
      this.activeDashboardTab.dashboardTabWidgets = [
        ...(this.activeDashboardTab.dashboardTabWidgets ?? []),
        {
          ...widget,
          widgetGuid: widget.guid,
          dashboardTabWidgetGuid: id,
          pendingRemoval: false,
          defaultJsonProperties: {
            ...widget.defaultJsonProperties,
            title: widget.title,
            subtitle: widget.subtitle,
          },
        },
      ];
    },
    removePreviewWidget() {
      const activeTab = this.activeDashboardTab;
      if (!activeTab) return;
      activeTab.dashboardTabWidgets = activeTab?.dashboardTabWidgets?.filter(
        (w) => w.dashboardTabWidgetGuid !== PREVIEW_WIDGET_KEY
      );
    },
    removeWidget(guid: string) {
      const activeTab = this.activeDashboardTab;
      if (!activeTab) return;

      this.dashboardTabs = this.dashboardTabs.map((tab) => {
        if (activeTab.guid !== tab.guid) return tab;
        const newTab = {
          ...tab,
          dashboardTabWidgets: (tab.dashboardTabWidgets || []).map((w) => ({
            ...w,
            pendingRemoval: w.dashboardTabWidgetGuid === guid ? true : w.pendingRemoval,
          })),
        };
        return newTab;
      });
    },
    async saveDashboardTab(tab: DashboardTab): Promise<boolean> {
      if (!tab.guid) return false;
      const tabWidgets = tab.dashboardTabWidgets?.filter((w) => w.dashboardTabWidgetGuid !== PREVIEW_WIDGET_KEY);

      const { toBeSaved, toBeRemoved } = (tabWidgets ?? []).reduce(
        (acc, w) => {
          if (w.pendingRemoval) acc.toBeRemoved.push(w);
          else acc.toBeSaved.push(w);
          return acc;
        },
        {
          toBeSaved: [] as DashboardWidget[],
          toBeRemoved: [] as DashboardWidget[],
        }
      );

      try {
        // Delete widgets marked for removal
        if (toBeRemoved.length > 0) {
          await Promise.all(
            toBeRemoved
              .filter((w: DashboardWidget) => !w.dashboardTabWidgetGuid.includes(PENDING_WIDGET_PREFIX))
              .map((w) =>
                useAxiosInstance.delete(`/api/dashboard/tabs/${tab.guid}/widgets/${w.dashboardTabWidgetGuid}/`)
              )
          );
        }

        // Save remaining widgets
        const { data } = await useAxiosInstance.put(`/api/dashboard/tabs/${tab.guid}`, {
          ...tab,
          guid: tab.guid,
          isCurrent: tab.isCurrent,
          jsonProperties: tab.jsonProperties,
          sortOrder: tab.sortOrder,
          dashboardTabWidgets: toBeSaved?.map((w) => {
            return {
              ...(!w.dashboardTabWidgetGuid.includes(PENDING_WIDGET_PREFIX) && {
                dashboardTabWidgetGuid: w.dashboardTabWidgetGuid,
              }),
              widgetGuid: w.widgetGuid,
              jsonProperties: JSON.stringify(w.defaultJsonProperties),
            };
          }),
        });

        if (!data.success) {
          const messageStore = useFlashMessageStore();
          messageStore.pushNotification({
            id: `widget-${tab.guid}-updated`,
            type: 'error',
            message: `Dashboard tab save failed`,
          });
        }

        if (toBeSaved.length > 0) {
          const dashboardTabWidgets = await this.fetchDashboardTabWidgets(tab.guid!);
          this.dashboardTabs = this.dashboardTabs.map((t) => (t.guid === tab.guid ? { ...t, dashboardTabWidgets } : t));
        }
        return true;
      } catch (error) {
        console.error('[DashboardStore] Error saving dashboard tab:', tab.guid, error);
        throw error;
      }
    },
    async deleteDashboardTab(tabId: string): Promise<boolean> {
      try {
        const { data } = await useAxiosInstance.delete(`/api/dashboard/tabs/${tabId}`);
        return data.success;
      } catch (error) {
        console.error('Error deleting dashboard tab:', error);
        return false;
      }
    },
    async addNewDashboardTab() {
      try {
        const { data } = await useAxiosInstance.post<ApiResponse<DashboardTab>>('/api/dashboard/tabs', {
          dashboardTabName: 'My New Dashboard',
          jsonProperties: '',
        });
        if (data.success && data.data) {
          const newTab = { ...data.data, dashboardTabWidgets: [], isCurrent: true };
          this.dashboardTabs = [
            ...this.dashboardTabs.map((d) => ({
              ...d,
              isCurrent: false,
            })),
            newTab,
          ];
          this.updateDashboardTabOrder(this.dashboardTabs);
        }
      } catch (error) {
        console.error('Error adding new dashboard tab:', error);
      }
    },
    async getDashboardTabs(): Promise<boolean> {
      try {
        const { data } = await useAxiosInstance.get<ApiResponse<DashboardTab[]>>('/api/dashboard/tabs');
        if (data.success) {
          const firstCurrentTab = data.data.find((tab) => tab.isCurrent);

          this.dashboardTabs = data.data.map((tab) => ({
            ...tab,
            synced: false,
            isCurrent: tab === firstCurrentTab,
          }));

          if (this.dashboardTabs.length > 0 && !this.activeDashboardTab) {
            // Use the current tab if found, otherwise use first tab
            const tabToUse = firstCurrentTab || this.dashboardTabs[0];
            await this.setActiveDashboardTab(tabToUse.guid!);
          } else {
            await this.syncActiveDashboardTabWidgets();
          }
        }
      } catch (error) {
        return false;
      }
      return true;
    },
    async updateDashboardTabOrder(newOrderedDashboards: DashboardTab[]) {
      this.dashboardTabs = newOrderedDashboards;
      try {
        const { data } = await useAxiosInstance.patch('/api/Dashboard/tabs/order', {
          currentDashboardTabGuid: this.activeDashboardTab?.guid,
          dashboardTabGuidsOrder: this.dashboardTabs
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((tab) => tab.guid),
        });
        return data.success;
      } catch (error) {
        console.error('Failed to update dashboard tab order:', error);
        return false;
      }
    },
    async syncActiveDashboardTabWidgets() {
      if (!this.dashboardTabs || this.dashboardTabs.length === 0) return;

      this.dashboardTabs = await Promise.all(
        this.dashboardTabs.map(async (tab: DashboardTab) => {
          if (tab.guid !== this.activeDashboardTab?.guid) return tab;
          const dashboardTabWidgets = await this.fetchDashboardTabWidgets(tab.guid!);
          return {
            ...tab,
            synced: true,
            dashboardTabWidgets,
          };
        })
      );
    },
    async fetchDashboardTabWidgets(guid: string): Promise<DashboardWidget[]> {
      try {
        const { data } = await useAxiosInstance.get<ApiResponse<{ dashboardTabWidgets: ApiDashboardTabWidget[] }>>(
          `/api/Dashboard/Tabs/${guid}/widgets`
        );
        if (data.success && data.data.dashboardTabWidgets) {
          return data.data.dashboardTabWidgets.map((w: ApiDashboardTabWidget) => ({
            widgetGuid: w.guid,
            dashboardTabWidgetGuid: w.dashboardTabWidgetGuid,
            defaultJsonProperties: w.defaultJsonProperties,
            dataSourceGuid: w.dataSourceGuid,
            pendingRemoval: false,
            helpFieldText: sanitize(w.helpFieldText),
          }));
        }
      } catch (error) {
        console.error(`[fetchDashboardTabWidgets] Failed for tab ${guid}:`, error);
      }
      return [];
    },
    updateWidgetLayout(widgetGuid: string, newLayout: Partial<LayoutItem>) {
      const activeTab = this.dashboardTabs.find((d) => d.isCurrent);
      if (!activeTab) return;

      const widget = activeTab.dashboardTabWidgets?.find((w) => w.dashboardTabWidgetGuid === widgetGuid);
      if (widget) {
        Object.assign(widget.defaultJsonProperties, newLayout);
      }
    },
  },
  getters: {
    activeDashboardTabLayout(state): LayoutItem[] | null {
      const activeTab = state.dashboardTabs.find((d) => d.isCurrent);
      if (!activeTab) return null;

      return (activeTab.dashboardTabWidgets ?? [])
        .filter((w) => !w.pendingRemoval)
        .map((w) => ({
          x: w.defaultJsonProperties.x ?? 0,
          y: w.defaultJsonProperties.y ?? 0,
          w: w.defaultJsonProperties.w ?? 4,
          h: w.defaultJsonProperties.h ?? 4,
          minW: w.defaultJsonProperties.minW,
          minH: w.defaultJsonProperties.minH,
          maxW: w.defaultJsonProperties.maxW,
          maxH: w.defaultJsonProperties.maxH,
          isDraggable: w.defaultJsonProperties.isDraggable,
          isResizable: w.defaultJsonProperties.isResizable,
          preserveAspectRatio: w.defaultJsonProperties.preserveAspectRatio,
          i: w.dashboardTabWidgetGuid,
        }));
    },
    hasPreviewWidget(state) {
      const activeTab = state.dashboardTabs.find((d) => d.isCurrent);
      return activeTab?.dashboardTabWidgets?.find((w) => w.dashboardTabWidgetGuid === PREVIEW_WIDGET_KEY);
    },
    getDashboardWidgetById: (state) => (id: string) => {
      const activeTab = state.dashboardTabs.find((d) => d.isCurrent);
      if (!activeTab) return null;
      return activeTab.dashboardTabWidgets?.find((w) => w.dashboardTabWidgetGuid === id) || null;
    },
    getDashboardWidgetParameters:
      (state) =>
      (id: string): Record<string, any> => {
        const activeTab = state.dashboardTabs.find((d) => d.isCurrent);
        if (!activeTab) return {};
        const widget = activeTab.dashboardTabWidgets?.find((w) => w.dashboardTabWidgetGuid === id);
        if (!widget) return {};
        const parameters = widget.defaultJsonProperties.parameters;
        if (!parameters) return {};

        const output: Record<string, any> = {};

        if (Array.isArray(parameters.filterBy)) {
          for (const filter of parameters.filterBy) {
            if (filter.key) {
              let value: any = null;
              if (typeof filter.value === 'number') value = String(filter.value);
              else if (Array.isArray(filter.value)) value = filter.value.includes('all') ? [] : [...filter.value];
              else value = filter.value;
              output[filter.key] = value;
            }
          }
        }

        if (Array.isArray(parameters.additionalFields)) {
          for (const field of parameters.additionalFields) {
            if (field.key) {
              let value: any = null;
              if (typeof field.value === 'number') value = String(field.value);
              else value = field.value;
              output[field.key] = value;
            }
          }
        }

        if (parameters.groupBy?.key) output[parameters.groupBy.key] = String(parameters.groupBy.value);
        if (parameters.orderBy?.key) output[parameters.orderBy.key] = String(parameters.orderBy.value);
        if (parameters.results != null) output.results = String(parameters.results);
        if (parameters.benchmarks?.values) output.benchmarks = parameters.benchmarks.values;
        if (parameters.weights?.values) output.weights = parameters.weights.values;

        const range = parameters.previousRange;
        const months = parameters.previousMonths;

        if (range?.selected && range?.value) {
          const [from, to] = range.value;

          output.previousRange =
            range.type === 'MY'
              ? [
                  from ? dayjs(from).startOf('month').format('YYYY-MM-DD') : '',
                  to ? dayjs(to).endOf('month').format('YYYY-MM-DD') : '',
                ]
              : range.value;
        } else if (months?.value != null) {
          output.previousMonths = Number(months.value);
        }
        if (parameters.withinXMonths?.value != null) output.withinXMonths = Number(parameters.withinXMonths.value);

        return output;
      },
    getWidgetById: (state) => (guid: string) => state.widgets.find((w) => w.guid === guid) || null,
    activeDashboardTab: (state) => state.dashboardTabs.find((tab) => tab.isCurrent),
    activeDashboardTabIndex: (state) => state.dashboardTabs.findIndex((tab) => tab.isCurrent),
    filteredWidgetSelection: (state): Widget[] => {
      let widgets = state.widgets;
      if (state.widgetCategoryFilter) {
        widgets = widgets.filter((w) => w.category === state.widgetCategoryFilter);
      }
      if (state.widgetFilter) {
        const filter = state.widgetFilter.toLowerCase();
        widgets = widgets.filter((w) => w.label?.toLowerCase().includes(filter));
      }
      return widgets;
    },
  },
});

export default useDashboardStore;
